/**
 * Refund Policy — compliant with Indian consumer law (Consumer Protection Act 2019)
 * + Razorpay merchant policy + GDPR consumer rights (right of withdrawal).
 */
import { LegalLayout, Section, List } from "./LegalLayout";

export default function RefundPolicy() {
  return (
    <LegalLayout
      title="Refund Policy"
      effectiveDate="5 February 2026"
      testid="refund-policy-page"
    >
      <p>
        This Refund Policy explains when and how you can request a refund for purchases
        made on FitFrik, including subscriptions (Pro and Elite) and trainer / nutritionist
        bookings. We want you to be happy with FitFrik — if you are not, this policy
        describes the remedies available to you.
      </p>

      <Section n="1" title="Subscriptions (Pro / Elite)">
        <List
          items={[
            "7-day money-back guarantee: Within 7 calendar days of your first subscription purchase, you can request a full refund for any reason. Email support@fitfrik.com or use Settings → Subscription → Request refund.",
            "After 7 days: Monthly and annual subscriptions are non-refundable for the current billing period. You can cancel at any time to prevent the next renewal.",
            "Annual plans: If you cancel an annual plan within the first 7 days you get a full refund. If you cancel after 7 days but within 30 days, we refund a pro-rata amount for unused months at our discretion.",
            "Renewal refunds: If an auto-renewal was charged unintentionally (e.g. you forgot to cancel), email us within 48 hours of the charge for a full refund — this is honoured as a one-time courtesy per account.",
            "Failed service: If FitFrik is materially unavailable for more than 72 consecutive hours within a paid month, you are entitled to a pro-rata refund.",
          ]}
        />
      </Section>

      <Section n="2" title="Trainer & nutritionist bookings">
        <p>
          Sessions booked with independent verified professionals are subject to the
          following rules:
        </p>
        <List
          items={[
            "Cancellation 24+ hours before the scheduled start time: 100% refund (minus a 2% payment gateway fee).",
            "Cancellation 4–24 hours before the scheduled start time: 50% refund.",
            "Cancellation less than 4 hours before the scheduled start time: no refund.",
            "No-show by the member: no refund.",
            "No-show by the professional: 100% refund. You can also request to re-schedule at no extra cost.",
            "Technical issue (server outage, failed video call infrastructure on our side): 100% refund + one complimentary credit to re-book with the same professional.",
          ]}
        />
      </Section>

      <Section n="3" title="FitPoints purchases">
        <p>
          FitPoints are a virtual loyalty currency. Purchases of FitPoints are
          <strong> non-refundable</strong> once credited to your account. Unused FitPoints
          do not expire while your account remains active.
        </p>
      </Section>

      <Section n="4" title="How to request a refund">
        <p>You can request a refund in either of the following ways:</p>
        <List
          items={[
            "In-app: Settings → Subscription → Request refund. Select the charge, the reason, and submit. Our team responds within 3 business days.",
            "Email: send your request from the email address on your FitFrik account to support@fitfrik.com. Include the payment reference number and the reason.",
          ]}
        />
        <p>
          Approved refunds are processed within <strong>5–7 business days</strong> back to
          the original payment method via Razorpay. Depending on your bank, funds may
          take an additional 2–5 business days to reflect on your statement.
        </p>
      </Section>

      <Section n="5" title="Chargebacks">
        <p>
          If you suspect an unauthorised or incorrect charge, please contact us first at
          support@fitfrik.com. We usually resolve issues faster than your bank's
          chargeback process. Filing a chargeback without first contacting us may result
          in temporary suspension of your account while the dispute is investigated.
        </p>
      </Section>

      <Section n="6" title="Taxes">
        <p>
          Where GST, VAT, or similar taxes were applied, those amounts are also refunded
          as required by law. You will receive a revised invoice reflecting the refund.
        </p>
      </Section>

      <Section n="7" title="Right of withdrawal (EEA / UK)">
        <p>
          If you are a consumer resident in the European Economic Area or the United
          Kingdom, you have a statutory right of withdrawal from a digital-services
          contract within 14 days of purchase. By starting a subscription and confirming
          immediate access to the Services, you expressly acknowledge that you lose this
          right once we have begun supplying the digital service. Notwithstanding this,
          our 7-day money-back guarantee above remains available to you.
        </p>
      </Section>

      <Section n="8" title="Changes to this Policy">
        <p>
          We may revise this Refund Policy from time to time. The version in effect on
          the date of your purchase governs that purchase. Material changes are announced
          in the app and by email at least 14 days in advance.
        </p>
      </Section>

      <Section n="9" title="Contact">
        <p>
          Any question about a refund? Email{" "}
          <a className="underline" href="mailto:support@fitfrik.com">
            support@fitfrik.com
          </a>{" "}
          with your account email and payment reference. We aim to respond within 1
          business day.
        </p>
      </Section>
    </LegalLayout>
  );
}
